export type TaskTimelineSource = "scheduled" | "status";

export type TaskTimelineInput = {
  date?: string | null;
  startMinutes?: number | null;
  durationMin?: number | null;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
};

export type TaskTimelineWindow = {
  start: Date;
  end: Date;
  durationMin: number;
  source: TaskTimelineSource;
};

export const MIN_TASK_WINDOW_MINUTES = 15;

function validDate(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function scheduledStart(input: TaskTimelineInput) {
  if (!input.date || input.startMinutes == null) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.date);
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    input.startMinutes,
  );
  return Number.isFinite(date.getTime()) ? date : null;
}

export function getTaskTimelineWindow(input: TaskTimelineInput): TaskTimelineWindow | null {
  const statusStart = validDate(input.startedAt);
  const statusEnd = validDate(input.completedAt);
  const fallbackDuration = Math.max(input.durationMin ?? 0, MIN_TASK_WINDOW_MINUTES);

  if (statusStart) {
    const validStatusEnd = statusEnd && statusEnd > statusStart ? statusEnd : null;
    const end = validStatusEnd
      ? validStatusEnd
      : new Date(statusStart.getTime() + fallbackDuration * 60_000);
    return {
      start: statusStart,
      end,
      durationMin: Math.round((end.getTime() - statusStart.getTime()) / 60_000),
      source: "status",
    };
  }

  const start = scheduledStart(input);
  if (!start) return null;
  const end = new Date(start.getTime() + fallbackDuration * 60_000);
  return {
    start,
    end,
    durationMin: fallbackDuration,
    source: "scheduled",
  };
}

export function isTaskTimelineActive(window: TaskTimelineWindow | null, now = new Date()) {
  if (!window) return false;
  const timestamp = now.getTime();
  return timestamp >= window.start.getTime() && timestamp < window.end.getTime();
}

export function compareTaskTimeline(
  a: TaskTimelineInput,
  b: TaskTimelineInput,
) {
  const aWindow = getTaskTimelineWindow(a);
  const bWindow = getTaskTimelineWindow(b);
  if (!aWindow && !bWindow) return 0;
  if (!aWindow) return 1;
  if (!bWindow) return -1;
  const aMinutes = aWindow.start.getHours() * 60 + aWindow.start.getMinutes();
  const bMinutes = bWindow.start.getHours() * 60 + bWindow.start.getMinutes();
  return aMinutes - bMinutes;
}
